/**
 * Out-of-stock CTA + back-in-stock alert (feature 6) — see blocks/_jd-stock-alert.liquid.
 *
 * Submits straight to CartBite's API rather than through the app. The app exposes
 * `initVar.sendSubscriptionRequest`, but its `fetch` is fire-and-forget — not awaited, no `.then`,
 * no `.catch`, no return value — so delegating to it makes "never fail silently" impossible. Posting
 * ourselves is the same request with a result we can act on.
 *
 * The contract was read off a real submission on 2026-08-11 (DevTools > Network, submit the app's
 * own modal, "Copy as cURL") and confirmed against the app bundle's `sendSubscriptionRequest`. It is
 * an internal API and may change without notice: everything variable is read from `window.growConfig`
 * at send time, and a rejected request surfaces in the modal. To redo the capture, see
 * .claude/specs/pdp-stock-alert.md.
 *
 * Prices go over the wire in currency units, not cents — the app divides by 100 before sending
 * (`processProduct`), so we do the same.
 */

const ENDPOINT = '/api/subscribers/campaignMapping';

const root = () => document.querySelector('[data-jd-stock-alert]');

/**
 * Sets the form's visible state.
 * @param {HTMLElement} form
 * @param {'idle' | 'sending' | 'sent' | 'error'} state
 * @param {string} [message]
 */
const setState = (form, state, message = '') => {
  const output = form.querySelector('[data-jd-stock-alert-message]');
  form.dataset.state = state;

  if (!output) return;
  output.textContent = message;
  output.hidden = !message;
  if (state === 'error') {
    output.dataset.state = 'error';
  } else {
    delete output.dataset.state;
  }
};

/**
 * Builds the subscription payload. Every product field is server-rendered onto the block for the
 * selected variant, so none of it can drift from what is on screen — except the price, which is
 * deliberately the catalogue one and not the discounted one the modal shows (see the block).
 * @param {HTMLElement} block
 * @param {string} email
 * @param {Record<string, any>} config
 */
const buildPayload = (block, email, config) => ({
  campaignUniqueId: config.stockFulFilledEmailCampaign.uniqueId,
  subscriberUniqueId: config.subscriberId,
  email,
  /* The app's own modal hides its newsletter checkbox and still sends `true`. The JD modal asks for
     nothing of the sort, so it opts nobody in. */
  optInToMarketing: false,
  subscriptionEvent: {
    productId: Number(block.dataset.productId),
    productPrice: Number(block.dataset.variantPriceCents) / 100,
    variantId: Number(block.dataset.variantId),
    variantPrice: Number(block.dataset.variantPriceCents) / 100,
    productTitle: block.dataset.productTitle,
    variantTitle: block.dataset.variantTitle,
    href: window.location.href,
    country: block.dataset.country || '',
    market: block.dataset.market || '',
  },
  locale: block.dataset.locale,
});

/**
 * Pushes the GA4 dataLayer event once CartBite has confirmed the subscription.
 * @param {HTMLElement} block
 * @param {number} price
 */
const pushBackInStockSignup = (block, price) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'back_in_stock_signup',
    ecommerce: {
      value: price,
      items: [
        {
          affiliation: block.dataset.affiliation || '',
          coupon: '',
          discount: 0,
          price,
          currency: block.dataset.currency || '',
          quantity: 1,
          index: 0,
          item_id: block.dataset.productId,
          item_name: block.dataset.productTitle,
          item_brand: block.dataset.itemBrand || '',
          item_category: block.dataset.itemCategory || '',
          item_category2: '',
          item_category3: '',
          item_category4: '',
          item_category5: '',
          item_list_id: '',
          item_list_name: '',
          item_variant: block.dataset.variantTitle,
        },
      ],
    },
    user_data: {
      user_id: window.customerId ?? null,
    },
  });
};

/**
 * @param {HTMLElement} block
 * @param {HTMLElement} form
 */
const submit = async (block, form) => {
  const input = form.querySelector('[data-jd-stock-alert-email]');
  if (!(input instanceof HTMLInputElement)) return;

  if (!input.checkValidity()) {
    input.setAttribute('aria-invalid', 'true');
    setState(form, 'error', form.dataset.messageInvalid);
    input.focus();
    return;
  }
  input.removeAttribute('aria-invalid');

  const config = window.growConfig;
  if (!config || !config.apiUrl || !config.subscriberId || !config.stockFulFilledEmailCampaign) {
    /* No app on the page — most often the CartBite app embed left disabled on this theme. Nothing
       can be registered, so say so rather than pretend the alert was taken. */
    setState(form, 'error', form.dataset.messageError);
    return;
  }

  setState(form, 'sending', '');

  try {
    const payload = buildPayload(block, input.value.trim(), config);
    const response = await fetch(`${config.apiUrl}${ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`CartBite responded ${response.status}`);
    setState(form, 'sent', form.dataset.messageSuccess);
    pushBackInStockSignup(block, payload.subscriptionEvent.variantPrice);
  } catch (error) {
    console.error('[jd-stock-alert]', error);
    setState(form, 'error', form.dataset.messageError);
  }
};

/**
 * Current on-screen bottom of the sticky header, so the blur veil starts exactly at its edge. The
 * announcement bar is static (it scrolls away) while the header row is sticky, so the header is
 * shorter once the page is scrolled — `--header-group-height`, the static full height the veil falls
 * back to in CSS, then leaves a band of unblurred page above the modal.
 *
 * Same measurement as the one in assets/jd-cart-modal.js, which cannot be imported: that file is a
 * classic-script IIFE. Kept in step by hand; if one changes, change both.
 */
const headerBottom = () => {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return 0;

  let bottom = 0;
  for (const child of headerGroup.children) {
    const rect = child.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < 200) bottom = Math.max(bottom, rect.bottom);
  }
  return bottom;
};

/**
 * Puts our CTA in the sticky bar whenever the selected variant is unavailable, so the bar offers the
 * alert instead of the disabled "Sold out" it renders natively (hidden in CSS). Called again after
 * every variant change: the native component morphs the whole bar, which drops anything we added.
 */
const syncStickyBar = () => {
  const block = root();
  const bar = document.querySelector('sticky-add-to-cart');
  if (!block || !bar) return;

  const existing = bar.querySelector('.jd-stock-alert__cta');
  if (bar.dataset.variantAvailable !== 'false') {
    existing?.remove();
    return;
  }
  if (existing) return;

  const template = block.querySelector('[data-jd-stock-alert-sticky-template]');
  const slot = bar.querySelector('.sticky-add-to-cart__button');
  if (!(template instanceof HTMLTemplateElement) || !slot) return;

  slot.after(document.importNode(template.content, true));
};

/* Delegated on the document: the block is replaced wholesale on every variant change, and the CTA
   also exists as a copy inside the sticky bar. There is one block per product page, so the modal is
   always the one under `root()` whichever CTA was clicked. */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const block = root();
  if (!block) return;

  if (event.target.closest('[data-jd-stock-alert-submit]')) {
    const form = block.querySelector('[data-jd-stock-alert-form]');
    if (form instanceof HTMLElement) submit(block, form);
    return;
  }

  /* Reopening starts from a clean slate: the modal is reused, so a previous confirmation or error
     would otherwise still be on screen. */
  if (event.target.closest('.jd-stock-alert__cta')) {
    const form = block.querySelector('[data-jd-stock-alert-form]');
    const input = block.querySelector('[data-jd-stock-alert-email]');
    if (form instanceof HTMLElement) setState(form, 'idle', '');
    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.removeAttribute('aria-invalid');
    }

    /* Measured on the click that opens the modal, not once at load: how much header is on screen
       depends on where the page is scrolled to. */
    const veil = block.querySelector('.jd-cart-modal__veil');
    if (veil instanceof HTMLElement) veil.style.insetBlockStart = `${headerBottom()}px`;
  }
});

/**
 * The native variant picker morphs only itself, so this block never re-renders on its own. The
 * variant response carries the whole re-rendered section, which already holds this block built for
 * the new variant — availability, price, size and packshot included. Swapping it in beats patching
 * fields one by one: no price formatting or discount maths duplicated in JS.
 */
document.addEventListener('variant:update', (event) => {
  const current = root();
  if (!current) return;

  const fresh = event.detail?.data?.html?.querySelector('[data-jd-stock-alert]');
  if (fresh) {
    current.replaceWith(document.importNode(fresh, true));
  } else {
    /* No fragment (some dispatchers only carry the resource): fall back to the one thing that must
       not lag behind — whether the CTA stands in for the buy buttons. */
    const variant = event.detail?.resource;
    if (variant) current.hidden = variant.available !== false;
  }

  /* Runs after the sticky component's own listener: it is bound to the section, we are on the
     document, so its morph has already happened by the time this fires. */
  syncStickyBar();
});

syncStickyBar();
